"use client";

import { useState } from "react";
import Link from 'next/link';
import Image from 'next/image';
import { ArrowLeft, Mail, HelpCircle, MessageSquare, Send, Loader2, CheckCircle } from 'lucide-react';

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Google Ads conversion tracking - fires both Contact and Submit Lead Form conversions
const trackContactConversion = () => {
  if (typeof window === "undefined") return;

  console.log("[Google Ads] Tracking contact form conversions...");

  if (window.gtag) {
    // Conversion 1: Contact
    window.gtag("event", "ads_conversion_Contact_Us_1", {});
    console.log("[Google Ads] Contact conversion fired");

    // Conversion 2: Submit lead form (JobWright Contact Form)
    window.gtag("event", "conversion", {
      send_to: "AW-11371837109/7DPdCFqmOuobELXtwa4q",
    });
    console.log("[Google Ads] Submit lead form conversion fired");
  } else if (window.dataLayer) {
    window.dataLayer.push({ event: "ads_conversion_Contact_Us_1" });
    window.dataLayer.push({
      event: "conversion",
      send_to: "AW-11371837109/7DPdCFqmOuobELXtwa4q",
    });
    console.log("[Google Ads] Conversions fired via dataLayer");
  }
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    website: "", // Honeypot field - bots fill this, humans don't see it
  });
  const [formLoadedAt] = useState(() => Date.now()); // Track when form was loaded
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      // Send to your API or email service
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          formLoadedAt, // Include timestamp for spam check
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      // Track Google Ads conversion
      trackContactConversion();

      setSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      setError("Failed to send message. Please email us directly at support@jobwright.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link href="/" className="block">
            <Image
              src="/jobwright-logo.png"
              alt="JobWright"
              width={180}
              height={45}
              className="h-9 w-auto"
            />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link
          href="/"
          className="inline-flex items-center text-[#05A0D1] hover:text-[#048DB8] mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        {/* Hero Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Get in Touch</h1>
          <p className="text-lg text-gray-600 mb-8">
            We&apos;re here to help! Reach out to us with any questions, concerns, or feedback.
          </p>

          {/* Contact Form */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Send us a message</h2>

            {submitted ? (
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Message sent!</p>
                  <p className="text-sm text-green-700">We&apos;ll get back to you within 24 hours.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05A0D1] focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05A0D1] focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05A0D1] focus:border-transparent"
                    placeholder="How can we help?"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05A0D1] focus:border-transparent resize-none"
                    placeholder="Tell us more about your question or feedback..."
                  />
                </div>

                {/* Honeypot field - hidden from humans, bots will fill it */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                >
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </div>

                {error && (
                  <p className="text-red-600 text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#05A0D1] text-white font-medium rounded-lg hover:bg-[#048DB8] transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Contact Methods */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Email Support */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-[#05A0D1] transition-colors">
              <div className="flex items-start mb-4">
                <div className="bg-[#05A0D1] rounded-lg p-3 mr-4">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Support</h3>
                  <p className="text-gray-600 mb-3">
                    For general inquiries and support questions
                  </p>
                  <a
                    href="mailto:support@jobwright.com"
                    className="text-[#05A0D1] hover:text-[#048DB8] font-medium transition-colors"
                  >
                    support@jobwright.com
                  </a>
                </div>
              </div>
            </div>

            {/* Help Center */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-[#05A0D1] transition-colors">
              <div className="flex items-start mb-4">
                <div className="bg-[#05A0D1] rounded-lg p-3 mr-4">
                  <HelpCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Help Center</h3>
                  <p className="text-gray-600 mb-3">
                    Browse our documentation and guides
                  </p>
                  <Link
                    href="/support"
                    className="text-[#05A0D1] hover:text-[#048DB8] font-medium transition-colors"
                  >
                    Visit Help Center
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Response Time */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <MessageSquare className="h-5 w-5 text-[#05A0D1] mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Response Times</h3>
                <p className="text-gray-700">
                  We typically respond to all inquiries within 24 hours during business days.
                  For urgent technical issues, please include &quot;URGENT&quot; in your subject line.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Not a customer yet?
              </h3>
              <p className="text-gray-600 mb-6">
                Start your free trial today and see why thousands of trade professionals trust JobWright
              </p>
              <Link
                href="/signup"
                className="inline-flex items-center justify-center px-6 py-3 bg-[#05A0D1] text-white font-medium rounded-lg hover:bg-[#048DB8] transition-colors"
              >
                Start Your Free Trial
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Product</h3>
              <ul className="space-y-2">
                <li><Link href="/features" className="text-gray-600 hover:text-[#05A0D1]">Features</Link></li>
                <li><Link href="/pricing" className="text-gray-600 hover:text-[#05A0D1]">Pricing</Link></li>
                <li><Link href="/security" className="text-gray-600 hover:text-[#05A0D1]">Security</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link href="/about" className="text-gray-600 hover:text-[#05A0D1]">About</Link></li>
                <li><Link href="/blog" className="text-gray-600 hover:text-[#05A0D1]">Blog</Link></li>
                <li><Link href="/contact" className="text-[#05A0D1] hover:text-[#048DB8]">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link href="/privacy" className="text-gray-600 hover:text-[#05A0D1]">Privacy Policy</Link></li>
                <li><Link href="/terms" className="text-gray-600 hover:text-[#05A0D1]">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link href="/support" className="text-gray-600 hover:text-[#05A0D1]">Help Center</Link></li>
                <li><a href="mailto:support@jobwright.com" className="text-gray-600 hover:text-[#05A0D1]">Email Support</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-200 text-center text-gray-600">
            <p>&copy; {new Date().getFullYear()} JobWright. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
